// Handles the common setup for different platform components.
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import getAPIHook from "./apis/getAPIHook";
import useEventCallbacks from "./useEventCallbacks";
import useErrorReportingCallback from "./useErrorReportingCallback";
import usePoster from "./usePoster";
import Deferred from "./utils/Deferred";
export default function useVideoSystem(_ref) {
  let {
    platform,
    // String of platform name
    apiLoader,
    // Async function that loads the platform's API
    poster: givenPoster,
    // A given poster src URL or element
    onEnded,
    onError: givenOnError,
    onLoadedMetadata,
    onPause,
    onPlay: givenOnPlay,
    onPlaying,
    onTimeUpdate,
    // The following should be wrapped in `useCallback`s:
    getEventDetail,
    // Handler to generate custom event detail object
    play,
    // Handler for imperatively playing the video
    pause,
    // Handler for imperatively pausing the video
    forceLibraryLoad
  } = _ref;
  // Conditions for only injecting the poster image and NOT the given video player script/library initially. This is for performance reasons in that many times the video is not the primary content on the page and we don't want to load the video player script/library until the user clicks on the video.
  const injectAndPlayOnClick = givenPoster && !forceLibraryLoad;

  // should we inject the player or not. Only inject immediately when logic to not inject immediately above is false. Otherwise we will wait for something to say to inject it
  const [injectVideoSupport, setInjectVideoSupport] = useState(!injectAndPlayOnClick);
  // When the needed library's injection is delayed we need to autoPlay the video once it is time to inject it. Track if we should do that or not
  const [playDelayedInjectedVideo, setPlayDelayedInjectedVideo] = useState(false);

  // Track if the poster was clicked to trigger if we should inject the video player script/library
  const [clicked, setClicked] = useState(false);
  const elementRef = useRef();
  const useAPI = getAPIHook(apiLoader, injectVideoSupport);

  // Whether the player is currently being built
  const [platformPlayerBuilding, setPlatformPlayerBuilding] = useState(false);
  const {
    loading: apiLoading,
    error: apiLoadingError
  } = useAPI();
  const buildPromiseRef = useRef(new Deferred());
  const onError = useErrorReportingCallback(platform, givenOnError);
  useEffect(() => {
    if (apiLoadingError) {
      onError(apiLoadingError);
    }
  }, [onError, apiLoadingError]);
  const {
    poster,
    hidePoster
  } = usePoster(givenPoster);
  const onPlay = useCallback(event => {
    hidePoster();
    if (typeof givenOnPlay === "function") {
      givenOnPlay(event);
    }
  }, [hidePoster, givenOnPlay]);

  // Should we play the video that was injected after clicking on the poster image?
  if (injectAndPlayOnClick && clicked && !playDelayedInjectedVideo) {
    setPlayDelayedInjectedVideo(true);
  }

  // Should we inject the video player script/library?
  if ((!givenPoster || playDelayedInjectedVideo) && play && !injectVideoSupport) {
    setInjectVideoSupport(true);
  }
  return {
    // A ref to be added to the root element
    elementRef,
    // Should we show the loading indicator or not?
    showLoader: !poster && apiLoading ||
    // No poster so show while loading library
    injectVideoSupport && poster && !forceLibraryLoad ||
    // Poster exists and library is loading (so a poster was clicked), but not if forceLibraryLoad is true which loads the library behind the poster (rare case)
    false,
    // Error in loading the API
    apiLoadingError,
    // Poster src URL or element to display if the player has not already played.
    poster,
    // The API for imperative control
    imperativeAPI: useMemo(() => {
      return {
        play: async () => {
          // If the player is not ready, inject it and wait for it to be ready
          if (injectAndPlayOnClick && !playDelayedInjectedVideo) {
            setClicked(true);
          } else {
            hidePoster();
            await buildPromiseRef.current.promise;
            play();
          }
        },
        pause: async () => {
          await buildPromiseRef.current.promise;
          pause();
        }
      };
    }, [hidePoster, buildPromiseRef, play, pause, injectAndPlayOnClick, playDelayedInjectedVideo]),
    // Whether the platform player is currently building
    platformPlayerBuilding,
    // Handler for setting the building state and then calling a given
    // build callback.
    buildPlatformPlayer: useCallback(async build => {
      setPlatformPlayerBuilding(true);
      if (!apiLoading && !apiLoadingError) {
        await build();
        setPlatformPlayerBuilding(false);
      }
    }, [apiLoading, apiLoadingError]),
    // Handler to call once the platform player is ready after building
    platformPlayerIsReady: useCallback(() => {
      buildPromiseRef.current.resolve();
    }, [buildPromiseRef]),
    // An object of the event handlers that have been wrapped to dispatch
    // the associated custom events
    eventHandlers: useEventCallbacks(platform, elementRef, {
      onEnded,
      onError,
      onLoadedMetadata,
      onPause,
      onPlay,
      onPlaying,
      onTimeUpdate
    }, getEventDetail),
    // Is the video player script/library injected?
    videoSupportReady: playDelayedInjectedVideo || !injectAndPlayOnClick,
    // Should the video autoplay due to delayed injection?
    videoAutoPlayFromDelayedInjection: playDelayedInjectedVideo
  };
}